import { Layout, Menu } from "antd";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import styles from "./Sidebar.module.scss";
import {
  BellOutlined,
  ControlOutlined,
  NotificationOutlined,
  DollarOutlined,
  SettingOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
  StopOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  TrophyOutlined,
  GiftOutlined,
} from "@ant-design/icons";

import { ROUTES } from "@routes/routes";

const TOP_LEVEL_ITEMS: { key: string; path: string }[] = [
  { key: "users", path: ROUTES.USERS },
  { key: "players", path: ROUTES.PLAYERS },
  { key: "notifications", path: ROUTES.NOTIFICATIONS },
  { key: "plans", path: ROUTES.PLANS },
  { key: "transactions", path: ROUTES.TRANSACTIONS },
  { key: "popups", path: ROUTES.POPUPS },
  { key: "store_banners", path: ROUTES.STORE_BANNERS },
  { key: "home_hero_cards", path: ROUTES.HOME_HERO_CARDS },
  { key: "tiers", path: ROUTES.TIERS },
  { key: "baselines", path: ROUTES.BASELINES },
  { key: "ranked_season_announcement", path: ROUTES.RANKED_SEASON_ANNOUNCEMENT },
  { key: "restricted_usernames", path: ROUTES.RESTRICTED_USERNAMES },
  { key: "configurations", path: ROUTES.CONFIGURATIONS },
  { key: "settings", path: ROUTES.SETTINGS },
];

const getMenuKeys = (pathname: string) => {
  if (pathname === ROUTES.CAMPAIGNS) {
    return { selectedKeys: ["affiliate-campaigns"], openKeys: ["affiliate"] };
  }
  if (pathname === ROUTES.CAMPAIGN_PROMO_CODES) {
    return { selectedKeys: ["affiliate-promo-codes"], openKeys: ["affiliate"] };
  }
  if (pathname === ROUTES.AFFILIATE_DAILY_REVENUE) {
    return { selectedKeys: ["affiliate-daily-revenue"], openKeys: ["affiliate"] };
  }

  const match = TOP_LEVEL_ITEMS.find((item) => pathname === item.path);
  return { selectedKeys: match ? [match.key] : [], openKeys: [] };
};

const menu = [
  {
    key: "users",
    icon: <UserOutlined />,
    label: <Link to={ROUTES.USERS}>Users</Link>,
  },
  {
    key: "players",
    icon: <TeamOutlined />,
    label: <Link to={ROUTES.PLAYERS}>Players</Link>,
  },
  {
    key: "notifications",
    icon: <BellOutlined />,
    label: <Link to={ROUTES.NOTIFICATIONS}>Notifications</Link>,
  },
  {
    key: "plans",
    icon: <UnorderedListOutlined />,
    label: <Link to={ROUTES.PLANS}>Plans</Link>,
  },
  {
    key: "transactions",
    icon: <DollarOutlined />,
    label: <Link to={ROUTES.TRANSACTIONS}>Transactions</Link>,
  },
  {
    key: "popups",
    icon: <NotificationOutlined />,
    label: <Link to={ROUTES.POPUPS}>Popups</Link>,
  },
  {
    key: "store_banners",
    icon: <InfoCircleOutlined />,
    label: <Link to={ROUTES.STORE_BANNERS}>Store Banners</Link>,
  },
  {
    key: "home_hero_cards",
    icon: <PictureOutlined />,
    label: <Link to={ROUTES.HOME_HERO_CARDS}>Home Hero Cards</Link>,
  },
  {
    key: "affiliate",
    icon: <GiftOutlined />,
    label: "Affiliate",
    children: [
      {
        key: "affiliate-campaigns",
        label: <Link to={ROUTES.CAMPAIGNS}>Campaigns</Link>,
      },
      {
        key: "affiliate-promo-codes",
        label: <Link to={ROUTES.CAMPAIGN_PROMO_CODES}>Promo codes</Link>,
      },
      {
        key: "affiliate-daily-revenue",
        label: <Link to={ROUTES.AFFILIATE_DAILY_REVENUE}>Daily revenue</Link>,
      },
    ],
  },
  {
    key: "tiers",
    icon: <TrophyOutlined />,
    label: <Link to={ROUTES.TIERS}>Tiers</Link>,
  },
  {
    key: "baselines",
    icon: <UnorderedListOutlined />,
    label: <Link to={ROUTES.BASELINES}>Global Baselines</Link>,
  },
  {
    key: "ranked_season_announcement",
    icon: <NotificationOutlined />,
    label: (
      <Link to={ROUTES.RANKED_SEASON_ANNOUNCEMENT}>Season announcement</Link>
    ),
  },
  {
    key: "restricted_usernames",
    icon: <StopOutlined />,
    label: <Link to={ROUTES.RESTRICTED_USERNAMES}>Restricted Usernames</Link>,
  },
  {
    key: "configurations",
    icon: <ControlOutlined />,
    label: <Link to={ROUTES.CONFIGURATIONS}>Configurations</Link>,
  },
  {
    key: "settings",
    icon: <SettingOutlined />,
    label: <Link to={ROUTES.SETTINGS}>Settings</Link>,
  },
];

const Sidebar = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const showDevItems = searchParams.has("dev");
  const selectedKeys = useMemo(
    () => getMenuKeys(pathname).selectedKeys,
    [pathname],
  );
  const [openKeys, setOpenKeys] = useState<string[]>(
    () => getMenuKeys(pathname).openKeys,
  );
  const menuItems = useMemo(
    () =>
      showDevItems
        ? menu
        : menu.filter((item) => item.key !== "ranked_season_announcement"),
    [showDevItems],
  );

  useEffect(() => {
    setOpenKeys(getMenuKeys(pathname).openKeys);
  }, [pathname]);

  return (
    <Layout.Sider width={270}>
      <Menu
        mode="inline"
        className={styles.menu}
        items={menuItems}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
      />
    </Layout.Sider>
  );
};

export default Sidebar;
